import { useState, useEffect, useRef } from 'react';
import { ChevronRight, FileText } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { SummaryEntry } from '@/lib/summaryParser';
import type { TreeNode } from '@/services/docsApi';

interface BookSidebarProps {
  repoName: string;
  summaryEntries: SummaryEntry[] | null;
  tree: TreeNode | null;
  selectedPath: string | null;
  onSelect: (path: string) => void;
}

const STORAGE_KEY = 'book-sidebar-collapsed';

function loadCollapsedState(): Record<string, boolean> {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
}

function saveCollapsedState(state: Record<string, boolean>) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

export function BookSidebar({ repoName, summaryEntries, tree, selectedPath, onSelect }: BookSidebarProps) {
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(loadCollapsedState);
  const activeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    saveCollapsedState(collapsed);
  }, [collapsed]);

  // Auto-scroll to active item
  useEffect(() => {
    if (activeRef.current) {
      activeRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [selectedPath]);

  const toggleCollapse = (key: string) => {
    setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  if (summaryEntries && summaryEntries.length > 0) {
    return (
      <nav className="py-2 text-sm">
        {summaryEntries.map((entry, i) => (
          <SummaryNode
            key={`${entry.title}-${i}`}
            entry={entry}
            depth={0}
            selectedPath={selectedPath}
            collapsed={collapsed}
            onToggle={toggleCollapse}
            onSelect={onSelect}
            activeRef={activeRef}
          />
        ))}
      </nav>
    );
  }

  if (tree) {
    return (
      <nav className="py-2 text-sm">
        {tree.children.map((child) => (
          <TreeNodeNav
            key={child.path}
            node={child}
            depth={0}
            selectedPath={selectedPath}
            collapsed={collapsed}
            onToggle={toggleCollapse}
            onSelect={onSelect}
            activeRef={activeRef}
          />
        ))}
      </nav>
    );
  }

  return <div className="p-4 text-sm text-slate-500">No content</div>;
}

// --- Summary-based navigation ---

function SummaryNode({
  entry,
  depth,
  selectedPath,
  collapsed,
  onToggle,
  onSelect,
  activeRef,
}: {
  entry: SummaryEntry;
  depth: number;
  selectedPath: string | null;
  collapsed: Record<string, boolean>;
  onToggle: (key: string) => void;
  onSelect: (path: string) => void;
  activeRef: React.RefObject<HTMLButtonElement | null>;
}) {
  const isGroup = !entry.path && entry.children.length > 0;
  const isCollapsed = collapsed[entry.title] ?? false;
  const isActive = entry.path === selectedPath;

  if (isGroup) {
    return (
      <div className="mt-4 first:mt-0">
        <button
          onClick={() => onToggle(entry.title)}
          className="flex items-center gap-1.5 w-full px-3 py-1.5 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider hover:text-slate-700 dark:hover:text-slate-300"
        >
          <ChevronRight className={cn('w-3 h-3 transition-transform shrink-0', !isCollapsed && 'rotate-90')} />
          {entry.title}
        </button>
        {!isCollapsed && entry.children.map((child, i) => (
          <SummaryNode
            key={`${child.title}-${i}`}
            entry={child}
            depth={depth + 1}
            selectedPath={selectedPath}
            collapsed={collapsed}
            onToggle={onToggle}
            onSelect={onSelect}
            activeRef={activeRef}
          />
        ))}
      </div>
    );
  }

  if (entry.path) {
    return (
      <>
        <button
          ref={isActive ? activeRef : undefined}
          onClick={() => onSelect(entry.path!)}
          className={cn(
            'flex items-center gap-2 w-full px-3 py-1.5 rounded-md transition-colors',
            isActive
              ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 font-medium'
              : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800',
          )}
          style={{ paddingLeft: `${depth * 12 + 12}px` }}
        >
          <FileText className="w-3.5 h-3.5 shrink-0" />
          <span className="truncate">{entry.title}</span>
        </button>
        {entry.children.length > 0 && entry.children.map((child, i) => (
          <SummaryNode
            key={`${child.title}-${i}`}
            entry={child}
            depth={depth + 1}
            selectedPath={selectedPath}
            collapsed={collapsed}
            onToggle={onToggle}
            onSelect={onSelect}
            activeRef={activeRef}
          />
        ))}
      </>
    );
  }

  return null;
}

// --- File-tree-based navigation ---

function TreeNodeNav({
  node,
  depth,
  selectedPath,
  collapsed,
  onToggle,
  onSelect,
  activeRef,
}: {
  node: TreeNode;
  depth: number;
  selectedPath: string | null;
  collapsed: Record<string, boolean>;
  onToggle: (key: string) => void;
  onSelect: (path: string) => void;
  activeRef: React.RefObject<HTMLButtonElement | null>;
}) {
  const isDir = node.type === 'dir';
  const isCollapsed = collapsed[node.path] ?? (depth >= 2);
  const isActive = !isDir && node.path === selectedPath;

  if (isDir) {
    return (
      <div className="mt-2 first:mt-0">
        <button
          onClick={() => onToggle(node.path)}
          className="flex items-center gap-1.5 w-full px-3 py-1 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider hover:text-slate-700 dark:hover:text-slate-300"
          style={{ paddingLeft: `${depth * 12 + 12}px` }}
        >
          <ChevronRight className={cn('w-3 h-3 transition-transform shrink-0', !isCollapsed && 'rotate-90')} />
          {node.name}
        </button>
        {!isCollapsed && node.children.map((child) => (
          <TreeNodeNav
            key={child.path}
            node={child}
            depth={depth + 1}
            selectedPath={selectedPath}
            collapsed={collapsed}
            onToggle={onToggle}
            onSelect={onSelect}
            activeRef={activeRef}
          />
        ))}
      </div>
    );
  }

  return (
    <button
      ref={isActive ? activeRef : undefined}
      onClick={() => onSelect(node.path)}
      className={cn(
        'flex items-center gap-2 w-full px-3 py-1.5 rounded-md transition-colors',
        isActive
          ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 font-medium'
          : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800',
      )}
      style={{ paddingLeft: `${depth * 12 + 12}px` }}
    >
      <FileText className="w-3.5 h-3.5 shrink-0" />
      <span className="truncate">{node.title || node.name}</span>
    </button>
  );
}
